import React from 'react';
import { Container, Stack } from 'react-bootstrap';
// @ts-ignore
import ZoomInIcon from '../../../../../assets/img/button-icons/MagnifyingGlassPlus.svg';
// @ts-ignore
import ZoomOutIcon from '../../../../../assets/img/button-icons/MagnifyingGlassMinus.svg';
import { AutomedicTimelineView, AutomedicTimelineViewLabel } from '../../types';
import ReactTooltip from 'react-tooltip';

interface TimelineHeaderProps {
  showOnlyActiveAlerts: boolean;
  toggleShowOnlyActiveAlerts: () => void;
  features: {
    automedicView?: boolean;
  };
  viewPoint: AutomedicTimelineView;
  onZoomOut: () => void;
  onZoomIn: () => void;
  gotoMinTime: () => void;
  onPrevClick: () => void;
  onNextClick: () => void;
  gotoMaxTime: () => void;
  onThisYear: () => void;
  updateTimeLineItems?: () => any;
  groupedItemsEvents?: any;
  onResetCurrentView?: () => void;
  visibleTimeStart?: number;
  visibleTimeEnd?: number;
  minTime?: number;
  maxTime?: number;
}

const TimelineHeader: React.FC<TimelineHeaderProps> = ({
  showOnlyActiveAlerts,
  toggleShowOnlyActiveAlerts,
  features,
  viewPoint,
  onZoomOut,
  onZoomIn,
  gotoMinTime,
  onPrevClick,
  onNextClick,
  gotoMaxTime,
  onThisYear,
  updateTimeLineItems,
  groupedItemsEvents,
  onResetCurrentView,
  visibleTimeStart,
  visibleTimeEnd,
  minTime,
  maxTime,
}) => {
  const handleZoomOut = () => {
    onZoomOut();
    if (updateTimeLineItems) {
      groupedItemsEvents = updateTimeLineItems();
    }
  };

  const handleZoomIn = () => {
    onZoomIn();
    if (updateTimeLineItems) {
      groupedItemsEvents = updateTimeLineItems();
    }
  };

  let prevTooltipText: string;
  switch (viewPoint) {
    case AutomedicTimelineView.MULTI_DAY_VIEW:
      prevTooltipText = 'Previous 3 days';
      break;
    case AutomedicTimelineView.WEEK_VIEW:
      prevTooltipText = 'Previous week';
      break;
    case AutomedicTimelineView.MONTH_VIEW:
      prevTooltipText = 'Previous month';
      break;
    case AutomedicTimelineView.MULTI_MONTH_VIEW:
      prevTooltipText = 'Previous page';
      break;
    default:
      prevTooltipText = 'Previous';
  }

  let nextTooltipText: string;
  switch (viewPoint) {
    case AutomedicTimelineView.MULTI_DAY_VIEW:
      nextTooltipText = 'Next 3 days';
      break;
    case AutomedicTimelineView.WEEK_VIEW:
      nextTooltipText = 'Next week';
      break;
    case AutomedicTimelineView.MONTH_VIEW:
      nextTooltipText = 'Next month';
      break;
    case AutomedicTimelineView.MULTI_MONTH_VIEW:
      nextTooltipText = 'Next page';
      break;
    default:
      nextTooltipText = 'Next';
  }

  const isMinTimeReached =
    minTime && visibleTimeStart && visibleTimeStart <= minTime;
  const isMaxTimeReached =
    maxTime && visibleTimeEnd && visibleTimeEnd >= maxTime;

  return (
    <Container fluid>
      <div id="divOverHeader" className="flexHoriz">
        <div
          className="flexItem"
          style={{ display: 'flex', alignItems: 'baseline' }}
        >
          <span className="heading2 mr-1">Timeline </span>
          <span className="form-check form-switch form-check-timeline hide-in-pdf">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={showOnlyActiveAlerts}
              onChange={toggleShowOnlyActiveAlerts}
            />
            <label
              className="form-check-label"
              htmlFor="flexSwitchCheckChecked"
              style={{ marginLeft: '10px', fontWeight: 400 }}
            >
              Show only active alerts
            </label>
          </span>
        </div>
        <div className="flexItem">
          <Stack direction="horizontal" gap={2}>
            {!features.automedicView ? (
              <>
                <a onClick={handleZoomOut}>
                  <img
                    src={ZoomOutIcon}
                    style={{ width: '18px' }}
                    alt="Zoom out icon"
                  />
                </a>
                <a onClick={handleZoomIn}>
                  <img
                    src={ZoomInIcon}
                    style={{ width: '18px' }}
                    alt="Zoom in icon"
                  />
                </a>
              </>
            ) : null}
            <ReactTooltip id="gotoPrevTooltip" place="top" type="dark">
              {prevTooltipText}
            </ReactTooltip>
            <ReactTooltip id="gotoNextTooltip" place="top" type="dark">
              {nextTooltipText}
            </ReactTooltip>
            <ReactTooltip id="gotoMinTimeTooltip" place="top" type="dark">
              First event
            </ReactTooltip>
            <ReactTooltip id="gotoMaxTimeTooltip" place="top" type="dark">
              Today
            </ReactTooltip>
            <a
              data-tip
              data-for="gotoMinTimeTooltip"
              onClick={!isMinTimeReached ? gotoMinTime : undefined}
              className={`hide-in-pdf ${isMinTimeReached ? 'disabled' : ''}`}
              style={
                isMinTimeReached
                  ? { opacity: 0.5, cursor: 'not-allowed' }
                  : undefined
              }
            >
              <i className="fa-solid fa-backward-step fa-lg"></i>
            </a>
            <a
              data-tip
              data-for="gotoPrevTooltip"
              onClick={!isMinTimeReached ? onPrevClick : undefined}
              className={`hide-in-pdf ${isMinTimeReached ? 'disabled' : ''}`}
              style={
                isMinTimeReached
                  ? { opacity: 0.5, cursor: 'not-allowed' }
                  : undefined
              }
            >
              <i className="fa-solid fa-backward fa-lg"></i>
            </a>
            <a
              data-tip
              data-for="gotoNextTooltip"
              onClick={!isMaxTimeReached ? onNextClick : undefined}
              className={`hide-in-pdf ${isMaxTimeReached ? 'disabled' : ''}`}
              style={
                isMaxTimeReached
                  ? { opacity: 0.5, cursor: 'not-allowed' }
                  : undefined
              }
            >
              <i className="fa-solid fa-forward fa-lg"></i>
            </a>
            <a
              data-tip
              data-for="gotoMaxTimeTooltip"
              onClick={!isMaxTimeReached ? gotoMaxTime : undefined}
              className={`hide-in-pdf ${isMaxTimeReached ? 'disabled' : ''}`}
              style={
                isMaxTimeReached
                  ? { opacity: 0.5, cursor: 'not-allowed' }
                  : undefined
              }
            >
              <i className="fa-solid fa-forward-step fa-lg"></i>
            </a>
            {features.automedicView ? (
              <>
                <button
                  style={{ marginLeft: '10px' }}
                  onClick={onZoomOut}
                  className="zoom-button hide-in-pdf"
                  disabled={
                    viewPoint === AutomedicTimelineView.MULTI_MONTH_VIEW
                  }
                >
                  <img
                    src={ZoomOutIcon}
                    style={{ width: '18px' }}
                    alt="Zoom out icon"
                  />
                </button>
                <button
                  onClick={onZoomIn}
                  className="zoom-button hide-in-pdf"
                  disabled={viewPoint === AutomedicTimelineView.MULTI_DAY_VIEW}
                >
                  <img
                    src={ZoomInIcon}
                    style={{ width: '18px' }}
                    alt="Zoom in icon"
                  />
                </button>
              </>
            ) : null}
            {features.automedicView ? (
              <button
                className="btn btnDarkGrey timelineMonthView btn-sm btn-xs"
                onClick={onResetCurrentView}
              >
                {AutomedicTimelineViewLabel[viewPoint]}
              </button>
            ) : (
              <a onClick={onThisYear} className="btn btnGrey btn-sm btn-xs">
                This year
              </a>
            )}
          </Stack>
        </div>
      </div>
    </Container>
  );
};

export default TimelineHeader;
